import { createClient } from "@supabase/supabase-js";
import type { Database } from "./types";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  throw new Error("Missing Supabase environment variables");
}

/**
 * Wraps fetch to detect Supabase 400 responses with code "session_expired".
 * When the refresh token expires (e.g. due to inactivity), Supabase returns
 * this instead of a 401, and the SDK doesn't always fire SIGNED_OUT.
 * We lazily import signOutOnSessionExpired to avoid a circular dependency
 * (this module is imported by the auth module).
 */
/** @internal Exported for testing. */
export async function fetchWithSessionExpiredCheck(...args: Parameters<typeof fetch>): Promise<Response> {
  const response = await fetch(...args);

  if (response.status === 400) {
    try {
      const cloned = response.clone();
      const body = await cloned.json();
      if (body?.code === "session_expired") {
        const { signOutOnSessionExpired } = await import("@/auth/signOutOnSessionExpired");
        signOutOnSessionExpired();
      }
    } catch {
      // Body wasn't JSON — not a session_expired error, ignore.
    }
  }

  return response;
}

export const supabase =
  /**
   * Msw cannot intercept the default cross-fetch
   * @link https://github.com/supabase/supabase/issues/27656
   */
  import.meta.env.MODE === "test"
    ? createClient<Database>(SUPABASE_URL!, SUPABASE_PUBLISHABLE_KEY!, {
        global: {
          fetch: (...args: Parameters<typeof fetch>) => {
            return fetch(...args);
          },
        },
      })
    : createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
        global: {
          fetch: fetchWithSessionExpiredCheck,
        },
      });
